import { useEffect, useState, useSyncExternalStore } from "react"
import { useLocation } from "@tanstack/react-router"

import { useBreakpoints } from "@productlane/ui"

import { isDevEnvironment } from "@/env"
import { useTrial } from "@/lib/useTrial"
import { useSelectionPillState } from "./selection-pill-state"

// Set by the bottom-right pill this bar replaced; a user who closed that
// already said no to the prompt.
const LEGACY_DISMISSED_KEY = "pl-ai-launcher-dismissed"
export const WIDGET_CONTAINER_SELECTOR =
	".ProductlaneWidget-MainWidget__Container"
// A beat after the page settles, so the bar does not arrive with the layout.
const SPOTLIGHT_DELAY_MS = 900

// Trial workspaces found the bar too much on every page, so it only shows
// while they set things up.
export function isSpotlightHiddenRoute(pathname: string): boolean {
	return pathname !== "/settings" && !pathname.startsWith("/settings/")
}

// What the spotlight bar offers on each settings section. `label` slots into
// "Ask about ___"; the longest matching prefix wins.
const PAGES: Array<{ prefixes: string[]; label: string; questions: string[] }> =
	[
		{
			prefixes: ["/settings/notifications"],
			label: "notifications",
			questions: ["What notifies me?", "Can I get them in Slack?"],
		},
		{
			prefixes: ["/settings/widget"],
			label: "the widget",
			questions: [
				"How do I install it?",
				"How do I identify users?",
				"How do I use spotlight?",
			],
		},
		{
			prefixes: ["/settings/emails", "/settings/signature"],
			label: "email",
			questions: ["How do I forward email?", "Can I use my own domain?"],
		},
		{
			prefixes: ["/settings/ai", "/settings/live-chat"],
			label: "the AI agent",
			questions: [
				"What does the agent read?",
				"Can I test it first?",
				"When does it hand off?",
			],
		},
		{
			prefixes: ["/settings/custom-connectors"],
			label: "custom connectors",
			questions: ["How do I connect my API?", "Which tools can it call?"],
		},
		{
			prefixes: ["/settings/integrations"],
			label: "integrations",
			questions: [
				"How do I connect Slack?",
				"How do I connect GitHub?",
				"How do I connect Linear?",
			],
		},
		{
			prefixes: ["/settings/portal", "/settings/support-portal"],
			label: "the portal",
			questions: ["Can I use my own domain?", "Can I run two portals?"],
		},
		{
			prefixes: ["/settings/plans"],
			label: "billing",
			questions: ["How is usage billed?", "Where are my invoices?"],
		},
		{
			prefixes: ["/settings/members"],
			label: "members",
			questions: ["How do I invite someone?", "What can each role do?"],
		},
		{
			prefixes: [
				"/settings/intercom-import",
				"/settings/zendesk-import",
				"/settings/csv-import",
				"/settings/contacts-csv-import",
				"/settings/docs-csv-import",
				"/settings/docs-markdown-import",
			],
			label: "imports",
			questions: ["What comes from Intercom?", "How do I import Zendesk?"],
		},
		{
			prefixes: [
				"/settings/sla",
				"/settings/assignments",
				"/settings/office-hours",
			],
			label: "the inbox",
			questions: ["How do SLAs work?", "Can threads auto-assign?"],
		},
		{
			prefixes: ["/settings"],
			label: "settings",
			questions: ["How do I set things up?", "How do I import my data?"],
		},
	]

const PAGE_PREFIXES = PAGES.flatMap((page) =>
	page.prefixes.map((prefix) => ({ prefix, page })),
).sort((a, b) => b.prefix.length - a.prefix.length)

export interface SpotlightPrompts {
	placeholder: string
	questions: string[]
}

export function spotlightPromptsFor(pathname: string): SpotlightPrompts {
	const page = PAGE_PREFIXES.find(
		({ prefix }) => pathname === prefix || pathname.startsWith(`${prefix}/`),
	)?.page
	return {
		placeholder: `Ask about ${page?.label ?? "this page"}`,
		questions: page?.questions ?? ["What can I do here?"],
	}
}

interface SpotlightApi {
	set?: (settings: { spotlightPrompts: SpotlightPrompts }) => void
	showSpotlight?: () => void
	hideSpotlight?: () => void
	setSpotlightOffsetX?: (offsetX: number) => void
}

function spotlightApi(): SpotlightApi | undefined {
	return (window as unknown as { Productlane?: SpotlightApi }).Productlane
}

function wasDismissed(): boolean {
	try {
		return localStorage.getItem(LEGACY_DISMISSED_KEY) === "true"
	} catch {
		return false
	}
}

// The widget mounts asynchronously and its "loaded" event can fire before any
// listener attaches, so detect readiness by the container's DOM presence.
export function useWidgetReady(): boolean {
	const [ready, setReady] = useState(
		() => !!document.querySelector(WIDGET_CONTAINER_SELECTOR),
	)

	useEffect(() => {
		if (ready) return
		const observer = new MutationObserver(() => {
			if (document.querySelector(WIDGET_CONTAINER_SELECTOR)) {
				setReady(true)
				observer.disconnect()
			}
		})
		observer.observe(document.body, { childList: true, subtree: true })
		return () => observer.disconnect()
	}, [ready])

	return ready
}

// Radix blocks pointer events on the body while a modal dialog is open, and the
// bar is a separate iframe above it, so it would cover the dialog's buttons.
export function useModalOpen(): boolean {
	const isOpen = () => document.body.style.pointerEvents === "none"
	const [open, setOpen] = useState(isOpen)

	useEffect(() => {
		const observer = new MutationObserver(() => setOpen(isOpen()))
		observer.observe(document.body, {
			attributes: true,
			attributeFilter: ["style"],
		})
		return () => observer.disconnect()
	}, [])

	return open
}

const spotlightAnchors = new Set<HTMLElement>()
const spotlightAnchorListeners = new Set<() => void>()

function notifySpotlightAnchors() {
	for (const listener of spotlightAnchorListeners) listener()
}

// Ref for the content column the bar centers under. Layouts remount it on
// navigation, so the launcher follows the live element instead of a stale one.
export function spotlightAnchorRef(element: HTMLElement | null) {
	if (!element) return
	spotlightAnchors.add(element)
	notifySpotlightAnchors()
	return () => {
		spotlightAnchors.delete(element)
		notifySpotlightAnchors()
	}
}

function subscribeSpotlightAnchor(listener: () => void) {
	spotlightAnchorListeners.add(listener)
	return () => spotlightAnchorListeners.delete(listener)
}

function currentSpotlightAnchor(): HTMLElement | null {
	let last: HTMLElement | null = null
	for (const anchor of spotlightAnchors) last = anchor
	return last
}

// Shows the widget's spotlight bar only for workspaces on trial or without an
// active paid subscription; paid workspaces just keep the default widget bubble.
export function AiWidgetLauncher() {
	const { isTrial, effectivePlan } = useTrial()
	const { pathname } = useLocation()
	const { isMobile } = useBreakpoints()
	const widgetReady = useWidgetReady()
	const [dismissed] = useState(wasDismissed)

	// Local dev workspaces usually run a paid plan, so dev always shows the bar.
	const eligible =
		isDevEnvironment || (!dismissed && (isTrial || effectivePlan === "free"))

	const selectionVisible = useSelectionPillState((s) => s.visible)
	const modalOpen = useModalOpen()
	const anchor = useSyncExternalStore(
		subscribeSpotlightAnchor,
		currentSpotlightAnchor,
	)

	const active =
		widgetReady &&
		eligible &&
		!isMobile &&
		!selectionVisible &&
		!modalOpen &&
		!isSpotlightHiddenRoute(pathname)

	useEffect(() => {
		if (!widgetReady) return
		if (!active) {
			spotlightApi()?.hideSpotlight?.()
			return
		}
		const timeout = setTimeout(
			() => spotlightApi()?.showSpotlight?.(),
			SPOTLIGHT_DELAY_MS,
		)
		return () => clearTimeout(timeout)
	}, [active, widgetReady])

	// Rides set(), which the orchestrator replays into the bar when it mounts.
	useEffect(() => {
		if (!active) return
		spotlightApi()?.set?.({ spotlightPrompts: spotlightPromptsFor(pathname) })
	}, [active, pathname])

	// The controller unmounts this on routes where the widget is off, and the
	// bar is its own iframe, so it has to be hidden on the way out.
	useEffect(() => () => spotlightApi()?.hideSpotlight?.(), [])

	// The sidebar pushes the content column off the viewport center, so the bar
	// centers under the column instead.
	useEffect(() => {
		if (!active) return
		if (!anchor) {
			spotlightApi()?.setSpotlightOffsetX?.(0)
			return
		}
		const measure = () => {
			const box = anchor.getBoundingClientRect()
			// A detached or hidden column reads 0x0, which would center the bar
			// on the viewport's left edge.
			if (box.width === 0) return
			spotlightApi()?.setSpotlightOffsetX?.(
				Math.round(box.left + box.width / 2 - window.innerWidth / 2),
			)
		}
		measure()
		const observer = new ResizeObserver(measure)
		observer.observe(anchor)
		window.addEventListener("resize", measure)
		return () => {
			observer.disconnect()
			window.removeEventListener("resize", measure)
		}
	}, [active, anchor])

	return null
}
